import { useRef, type KeyboardEvent } from "react";
import { FileText, Library, X, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTabs, useUi, type Tab } from "@/lib/store";
import { useLiveTitles } from "@/features/crypto/UnlockPanel";

function tabIcon(t: Tab) {
  if (t.kind === "paper") return <Library className="size-3.5 shrink-0" aria-hidden />;
  if (t.kind === "view") return <LayoutDashboard className="size-3.5 shrink-0" aria-hidden />;
  return <FileText className="size-3.5 shrink-0" aria-hidden />;
}

/** One horizontal tab bar of open documents and views. */
export function TabBar({ narrow }: { narrow: boolean }) {
  // Decrypted titles of encrypted notes are only held in memory (tabs are persisted with a placeholder).
  const liveTitles = useLiveTitles((st) => st.titles);
  const liveTitle = (t: { kind: string; id?: string; title: string }) => (t.kind === "note" && t.id && liveTitles[t.id]) || t.title;
  const { tabs, activeKey } = useTabs();
  const activate = useUi((s) => s.activate);
  const closeTab = useUi((s) => s.closeTab);
  const listRef = useRef<HTMLDivElement>(null);

  if (!tabs.length) return null;

  if (narrow) {
    return (
      <div className="flex items-center gap-2 border-b bg-background px-3 py-1.5">
        <label htmlFor="open-docs" className="text-xs text-muted-foreground">
          Open
        </label>
        <select
          id="open-docs"
          className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-sm"
          value={activeKey ?? ""}
          onChange={(e) => activate(e.target.value)}
        >
          {tabs.map((t) => (
            <option key={t.key} value={t.key}>
              {liveTitle(t)}
            </option>
          ))}
        </select>
        {activeKey && (
          <button type="button" className="rounded p-1 hover:bg-accent" aria-label="Close current document" onClick={() => closeTab(activeKey)}>
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  }

  const onKey = (e: KeyboardEvent, idx: number) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const next = tabs[(idx + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length];
      activate(next.key);
      requestAnimationFrame(() => listRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(next.key)}"]`)?.focus());
    } else if (e.key === "Delete") {
      e.preventDefault();
      closeTab(tabs[idx].key);
    }
  };

  return (
    <div ref={listRef} role="tablist" aria-label="Open documents" className="thin-scroll flex shrink-0 items-end gap-0.5 overflow-x-auto border-b bg-sidebar px-2 pt-1.5">
      {tabs.map((t, i) => {
        const active = t.key === activeKey;
        return (
          <div
            key={t.key}
            className={cn(
              "group flex max-w-[16rem] shrink-0 items-center rounded-t-md border border-b-0 text-sm",
              active ? "border-border bg-background" : "border-transparent text-muted-foreground hover:bg-accent/60",
            )}
          >
            <button
              type="button"
              role="tab"
              data-key={t.key}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => activate(t.key)}
              onAuxClick={(e) => e.button === 1 && closeTab(t.key)}
              onKeyDown={(e) => onKey(e, i)}
              className="flex min-w-0 items-center gap-1.5 py-1.5 pl-2.5 pr-1"
              title={liveTitle(t)}
            >
              {tabIcon(t)}
              <span className="truncate">{liveTitle(t)}</span>
            </button>
            <button
              type="button"
              onClick={() => closeTab(t.key)}
              aria-label={`Close ${liveTitle(t)}`}
              className="mr-1 rounded p-0.5 opacity-60 hover:bg-accent hover:opacity-100 focus-visible:opacity-100"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
